import { getLanguage } from '../user/device-info';
import { handlePagePopupClose } from '../popups/close-handler';
import triggerEvent, { DiscardAction, DiscardEvent, GAEventName } from '../analytics/ga/events';
import { parseEscapedJSONString } from '../json/parse';
import { AddToCartEvent } from '../analytics/ga/ecommerce';
class DiscardSaveResponse {
    html: string;
}

class Discardable {
    constructor() {
        this.init();
    }

    init() {
        window.addEventListener( 'click', this.handleClick.bind( this ) );
    }

    async handleClick( e: Event ) {
        const target = e.target as HTMLElement | null;

        if ( !target || !target.dataset.discard || target.dataset.discard !== 'true' ) {
            return;
        }

        const property = target.dataset.discardProperty as string;
        const value = target.dataset.discardValue as string;
        const action = target.dataset.discardAction as string;

        if ( action === 'remove' ) {
            await this.remove( property, value );
        } else {
            await this.save( property, value );
        }

        window.dispatchEvent( new CustomEvent( 'twc:discarded-updated', {
            bubbles: true,
            detail: {
                discarded: action !== 'remove',
                property: property,
                value: value,
            },
        } ) );

        // discarding item will affect displayed item if it was saved
        window.dispatchEvent( new CustomEvent( 'twc:update-saved-status', {
            bubbles: true,
        } ) );

        const inPopupActions = target.closest<HTMLElement>( '.popup-actions' );

        if ( inPopupActions ) {
            handlePagePopupClose();
        }
    }

    async save( property: string, value: string ): Promise<void> {
        const form = new FormData();
        form.append( 'property', property );
        form.append( 'value', value );

        let langPrefix = '';

        const lang = getLanguage();

        if ( lang !== 'en' ) {
            langPrefix = '/' + lang;
        }

        const url = new URL( window.location.origin + langPrefix + '/discarded/save' );

        const response = await window.http( url, {
            method: 'POST',
            body: form,
        } );

        if ( !response.ok ) {
            throw new Error( 'failed saving discarded items with status ' + response.status );
        }

        const relatedSavedButton = document.querySelector( `button[data-save-property="${property}"][data-save-value="${value}"]` ) as HTMLButtonElement | null;

        if ( relatedSavedButton && relatedSavedButton.dataset.addToCartEvent ) {
            const ecommerceEventData = parseEscapedJSONString( relatedSavedButton.dataset.addToCartEvent ) as AddToCartEvent;

            triggerEvent<DiscardEvent>( GAEventName.ItemDiscard, {
                action: DiscardAction.Discard,
                'item_id': ecommerceEventData.ecommerce.items[ 0 ].item_id,
                'item_name': ecommerceEventData.ecommerce.items[ 0 ].item_name,
                'item_brand': ecommerceEventData.ecommerce.items[ 0 ].item_brand,
                'item_variant': ecommerceEventData.ecommerce.items[ 0 ].item_variant,
                'item_category': ecommerceEventData.ecommerce.items[ 0 ].item_category,
                'item_category2': ecommerceEventData.ecommerce.items[ 0 ].item_category2,
                'item_category3': ecommerceEventData.ecommerce.items[ 0 ].item_category3,
                'item_category4': ecommerceEventData.ecommerce.items[ 0 ].item_category4,
                'item_category5': ecommerceEventData.ecommerce.items[ 0 ].item_category5,
            } );
        }

        const jsonResponse = await response.json() as DiscardSaveResponse;

        const selector = `[data-property-id="${value}"]`;

        document.querySelectorAll<HTMLElement>( selector ).forEach( ( element: HTMLElement ) => {
            const isOnDiscardedTab = element.closest( '#discarded_tab' ) !== null;

            element.classList.add( 'discarded' );
            element.classList.remove( 'recovered' );

            element.querySelectorAll( '.overlay' ).forEach( ( overlay: HTMLElement ) => overlay.remove() );

            if ( isOnDiscardedTab ) {
                return;
            }

            element.querySelectorAll( 'article' ).forEach( ( article: HTMLElement ) => {
                article.insertAdjacentHTML( 'beforebegin', `<div class="overlay">${jsonResponse.html}</div>` );
            } );
        } );
    }

    async remove( property: string, value: string ) {
        const form = new FormData();
        form.append( 'property', property );
        form.append( 'value', value );

        let langPrefix = '';

        const lang = getLanguage();

        if ( lang !== 'en' ) {
            langPrefix = '/' + lang;
        }

        const url = new URL( window.location.origin + langPrefix + '/discarded/remove' );

        const response = await window.http( url, {
            method: 'POST',
            body: form,
        } );

        if ( !response.ok ) {
            throw new Error( 'failed removing discarded items with status ' + response.status );
        }

        let eventParams = {
            action: DiscardAction.Recover,
        };

        const relatedSavedButton = document.querySelector( `button[data-save-property="${property}"][data-save-value="${value}"]` ) as HTMLButtonElement | null;

        if ( relatedSavedButton && relatedSavedButton.dataset.addToCartEvent ) {
            const ecommerceEventData = parseEscapedJSONString( relatedSavedButton.dataset.addToCartEvent ) as AddToCartEvent;

            if ( ecommerceEventData && ecommerceEventData.ecommerce && ecommerceEventData.ecommerce.items ) {
                eventParams = {
                    ...eventParams,
                    ...{
                        'item_id': ecommerceEventData.ecommerce.items[ 0 ].item_id,
                        'item_name': ecommerceEventData.ecommerce.items[ 0 ].item_name,
                        'item_brand': ecommerceEventData.ecommerce.items[ 0 ].item_brand,
                        'item_variant': ecommerceEventData.ecommerce.items[ 0 ].item_variant,
                        'item_category': ecommerceEventData.ecommerce.items[ 0 ].item_category,
                        'item_category2': ecommerceEventData.ecommerce.items[ 0 ].item_category2,
                        'item_category3': ecommerceEventData.ecommerce.items[ 0 ].item_category3,
                        'item_category4': ecommerceEventData.ecommerce.items[ 0 ].item_category4,
                        'item_category5': ecommerceEventData.ecommerce.items[ 0 ].item_category5,
                    },
                };
            }
        }

        triggerEvent<DiscardEvent>( GAEventName.ItemDiscard, eventParams );

        const jsonResponse = await response.json() as DiscardSaveResponse;

        const selector = `[data-property-id="${value}"]`;

        document.querySelectorAll<HTMLElement>( selector ).forEach( ( element: HTMLElement ) => {
            const isOnDiscardedTab = element.closest( '#discarded_tab' ) !== null;

            element.classList.remove( 'discarded' );
            element.classList.add( 'recovered' );

            element.querySelectorAll( '.overlay' ).forEach( ( overlay: HTMLElement ) => overlay.remove() );

            // we do not remove the overlay if the property is on the discarded tab
            if ( !isOnDiscardedTab ) {
                return;
            }

            element.querySelectorAll( 'article' ).forEach( ( article: HTMLElement ) => {
                article.insertAdjacentHTML( 'beforebegin', `<div class="overlay">${jsonResponse.html}</div>` );
            } );
        } );
    }
}

export default Discardable;
